"use client";

import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations, useLocale } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const PRODUCTS = [
    { id: 1, nameKey: "Sf_doctrine_p1", prix: 49 },
    { id: 2, nameKey: "Sf_doctrine_p2", prix: 19 },
    { id: 3, nameKey: "Sf_doctrine_p3", prix: 149 },
    { id: 4, nameKey: "Sf_doctrine_p4", prix: 12 },
];

const TOKEN = {
    sqlkw: "text-fuchsia-400",
    ident: "text-sky-200",
    num: "text-amber-300",
    op: "text-zinc-400",
    star: "text-zinc-200",
    var: "text-sky-200",
    arrow: "text-zinc-400",
    fn: "text-sky-400",
    str: "text-emerald-300",
    punct: "text-zinc-500",
};

const OPS = [
    {
        id: "all",
        labelKey: "Sf_doctrine_op_all",
        sql: [
            ["SELECT ", "sqlkw"],
            ["*", "star"],
            [" FROM ", "sqlkw"],
            ["produit", "ident"],
        ],
        doctrine: [
            ["$produits", "var"],
            [" = ", "op"],
            ["$repo", "var"],
            ["->", "arrow"],
            ["findAll", "fn"],
            ["()", "punct"],
        ],
        run: (list) => list,
    },
    {
        id: "find",
        labelKey: "Sf_doctrine_op_find",
        sql: [
            ["SELECT ", "sqlkw"],
            ["*", "star"],
            [" FROM ", "sqlkw"],
            ["produit", "ident"],
            [" WHERE ", "sqlkw"],
            ["id", "ident"],
            [" = ", "op"],
            ["2", "num"],
        ],
        doctrine: [
            ["$produit", "var"],
            [" = ", "op"],
            ["$repo", "var"],
            ["->", "arrow"],
            ["find", "fn"],
            ["(", "punct"],
            ["2", "num"],
            [")", "punct"],
        ],
        run: (list) => list.filter((p) => p.id === 2),
    },
    {
        id: "cheap",
        labelKey: "Sf_doctrine_op_cheap",
        sql: [
            ["SELECT ", "sqlkw"],
            ["*", "star"],
            [" FROM ", "sqlkw"],
            ["produit", "ident"],
            [" WHERE ", "sqlkw"],
            ["prix", "ident"],
            [" < ", "op"],
            ["20", "num"],
        ],
        doctrine: [
            ["$produits", "var"],
            [" = ", "op"],
            ["$repo", "var"],
            ["->", "arrow"],
            ["findCheaperThan", "fn"],
            ["(", "punct"],
            ["20", "num"],
            [")", "punct"],
        ],
        run: (list) => list.filter((p) => p.prix < 20),
    },
    {
        id: "sort",
        labelKey: "Sf_doctrine_op_sort",
        sql: [
            ["SELECT ", "sqlkw"],
            ["*", "star"],
            [" FROM ", "sqlkw"],
            ["produit", "ident"],
            [" ORDER BY ", "sqlkw"],
            ["prix", "ident"],
        ],
        doctrine: [
            ["$produits", "var"],
            [" = ", "op"],
            ["$repo", "var"],
            ["->", "arrow"],
            ["findBy", "fn"],
            ["([], [", "punct"],
            ["'prix'", "str"],
            [" => ", "op"],
            ["'ASC'", "str"],
            ["])", "punct"],
        ],
        run: (list) => [...list].sort((a, b) => a.prix - b.prix),
    },
];

const rowContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06 } },
};
const rowItem = {
    hidden: { opacity: 0, x: -8 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: "easeOut" } },
};

export default function SfDoctrine() {
    const t = useTranslations();
    const locale = useLocale();
    const reduce = useReducedMotion();

    const [opId, setOpId] = useState("all");
    const [mode, setMode] = useState("doctrine"); // sql | doctrine

    const op = OPS.find((o) => o.id === opId);
    const rows = op.run(PRODUCTS);
    const tokens = mode === "sql" ? op.sql : op.doctrine;

    function price(p) {
        return locale === "en" ? `$${p}` : `${p} €`;
    }

    return (
        <section id="symfony-doctrine" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Sf_doctrine_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Sf_doctrine_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows monochromes */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-white/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-white/5 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="simple-icons:symfony"
                            className="w-3.5 h-3.5 text-white"
                            aria-hidden="true"
                        />
                        ProduitRepository.php
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] ring-1 ring-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-300">
                        Doctrine ORM
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Opérations */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        {OPS.map((o) => (
                            <button
                                key={o.id}
                                type="button"
                                onClick={() => setOpId(o.id)}
                                className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition active:scale-95 ${
                                    opId === o.id
                                        ? "bg-white text-bgDark-900"
                                        : "bg-white/[0.06] ring-1 ring-white/15 text-gray-200 hover:bg-white/[0.1]"
                                }`}
                            >
                                {t(o.labelKey)}
                            </button>
                        ))}
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
                        {/* CODE */}
                        <div className="min-w-0">
                            {/* Bascule SQL / Doctrine */}
                            <div className="inline-flex rounded-full bg-black/30 ring-1 ring-white/10 p-1 mb-3">
                                {[
                                    { key: "sql", labelKey: "Sf_doctrine_mode_sql" },
                                    {
                                        key: "doctrine",
                                        labelKey: "Sf_doctrine_mode_doctrine",
                                    },
                                ].map((m) => (
                                    <button
                                        key={m.key}
                                        type="button"
                                        onClick={() => setMode(m.key)}
                                        className={`rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition ${
                                            mode === m.key
                                                ? "bg-white text-bgDark-900"
                                                : "text-gray-400 hover:text-gray-200"
                                        }`}
                                    >
                                        {t(m.labelKey)}
                                    </button>
                                ))}
                            </div>

                            <div className="rounded-2xl bg-black/30 ring-1 ring-white/10 overflow-hidden">
                                <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-white/[0.03]">
                                    <SolarIcon
                                        name={
                                            mode === "sql"
                                                ? "database-bold-duotone"
                                                : "code-square-bold"
                                        }
                                        className="w-4 h-4 text-white"
                                    />
                                    <span className="font-mono text-[11.5px] text-gray-300">
                                        {mode === "sql"
                                            ? t("Sf_doctrine_tab_sql")
                                            : t("Sf_doctrine_tab_doctrine")}
                                    </span>
                                </div>
                                <div className="p-4 min-h-[84px] flex items-center">
                                    <AnimatePresence mode="wait">
                                        <motion.pre
                                            key={mode + opId}
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0, y: 6 }
                                            }
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="font-mono text-[13px] leading-relaxed whitespace-pre-wrap break-words"
                                        >
                                            {tokens.map(([text, type], i) => (
                                                <span
                                                    key={i}
                                                    className={TOKEN[type]}
                                                >
                                                    {text}
                                                </span>
                                            ))}
                                            {mode === "doctrine" && (
                                                <span className="text-zinc-500">
                                                    ;
                                                </span>
                                            )}
                                        </motion.pre>
                                    </AnimatePresence>
                                </div>
                            </div>

                            <div className="mt-3 flex items-start gap-2 text-[12.5px] text-gray-300/90">
                                <SolarIcon
                                    name="magic-stick-3-bold"
                                    className="w-4 h-4 text-white shrink-0 mt-0.5"
                                />
                                <span>{t("Sf_doctrine_note")}</span>
                            </div>
                        </div>

                        {/* RÉSULTAT */}
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                                <SolarIcon
                                    name="checklist-minimalistic-bold-duotone"
                                    className="w-3.5 h-3.5"
                                />
                                {t("Sf_doctrine_result_label")}
                            </div>

                            <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                                    <span>{t("Sf_doctrine_col_name")}</span>
                                    <span>{t("Sf_doctrine_col_price")}</span>
                                </div>
                                <motion.div
                                    key={opId}
                                    variants={reduce ? undefined : rowContainer}
                                    initial={reduce ? false : "hidden"}
                                    animate="visible"
                                    className="divide-y divide-white/5"
                                >
                                    {rows.map((p) => (
                                        <motion.div
                                            key={p.id}
                                            variants={reduce ? undefined : rowItem}
                                            className="flex items-center justify-between px-4 py-2.5"
                                        >
                                            <span className="flex items-center gap-2 text-[13.5px] text-gray-100">
                                                <span className="flex items-center justify-center w-5 h-5 rounded-md bg-white/10 text-[10px] font-mono text-gray-400">
                                                    {p.id}
                                                </span>
                                                {t(p.nameKey)}
                                            </span>
                                            <span className="font-semibold text-white tabular-nums">
                                                {price(p.prix)}
                                            </span>
                                        </motion.div>
                                    ))}
                                </motion.div>
                                <div className="px-4 py-2 border-t border-white/10 text-[11.5px] text-gray-400">
                                    {t("Sf_doctrine_rows", { count: rows.length })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Sf_doctrine_caption")}
            </p>
        </section>
    );
}
